'use client';

import { ArticlePlanningData } from '../articles/create/components/ManualMode';
import { AIGeneratedArticle } from '../articles/create/components/AIArticleSuggestions';
import { apiClient } from '@/app/api/client';

// Interface for title generation request
export interface TitleGenerationRequest {
  content: string;
  tags?: string[];
  direction?: string;
  profileId?: string; // Optional: Pin to a specific persona
}

// Interface for title generation response
export interface TitleGenerationResponse {
  titles: string[];
  reasoning?: string;
}

import { ChatMessage } from '@contentgrowth/llm-service/ui/react';
export type { ChatMessage };

export interface ArticleChatRequest {
  messages: ChatMessage[];
  id?: string; // Optional: Message ID for cancellation tracking
}

export interface ArticleChatResponse {
  content: string;
}

// Interfaces for Article AI Editing
export interface ArticleEditRequest {
  articleId: string;
  messages: ChatMessage[];
  instruction: string;
  articleTitle?: string;
  profileId?: string; // Optional: Pin to a specific persona
  selection?: {
    text: string;
    prefix: string;
    suffix: string;
  } | null;
  cursorContext?: {
    before: string;
    after: string;
    paragraphBefore: string;
    paragraphAfter: string;
    isEmptyParagraph: boolean;
  } | null;
}

export interface HistoryItem {
  diff: string;
  type: 'ai' | 'user';
}

export interface UndoableSession {
  history: HistoryItem[];
  pointer: number;
  chatMessages: ChatMessage[];
}

export interface AddChangeRequest {
  diff: string;
  message?: ChatMessage;
  type: 'ai' | 'user';
}

export interface EditArticleResponse {
  content: string;
  message: string;
  hasContentChanges: boolean;
  suggestedTitle?: string;
  changeSummary?: string;
  replacement?: string;
  references?: Record<string, { type: string; uuid?: string; category?: string; slug?: string }> | null;
}

export type SharePlatform = 'linkedin' | 'twitter' | 'facebook' | 'reddit' | 'whatsapp' | 'email' | 'copy';

export interface ShareTextRequest {
  articleId: string;
  platform: SharePlatform;
  targetUrl?: string;
}

export interface ShareTextResponse {
  text: string;
}

/**
 * API client for AI article generation
 */
export const articleAiApi = {
  /**
   * Generate article suggestions based on planning data
   * @param planningData The article planning data
   * @returns AI-generated article suggestions
   */
  generateArticle: async (planningData: ArticlePlanningData): Promise<AIGeneratedArticle> => {
    try {
      const payload = { ...planningData };
      if (payload.desiredLength && !payload.length) {
        payload.length = payload.desiredLength;
      }
      delete payload.desiredLength; // Clean up the property before sending

      // console.log('Making API call to /api/gen/schema with payload:', payload);

      // Make API call to the schema generation endpoint using apiClient for authentication
      const response = await apiClient.post('/api/gen/schema', payload);

      // console.log('API response received:', response.data);

      // Handle the case where the API returns a content property with a JSON string
      let parsedData;
      if (response.data && response.data.content && typeof response.data.content === 'string') {
        try {
          // Parse the JSON string in the content property
          parsedData = JSON.parse(response.data.content);
          // console.log('Successfully parsed content JSON:', parsedData);
        } catch (parseError) {
          console.error('Error parsing content JSON:', parseError);
          throw new Error('Failed to parse API response content');
        }
      } else {
        // If the response is already in the expected format
        parsedData = response.data;
      }

      // Validate the parsed data
      if (!parsedData || !parsedData.title || !parsedData.outline || !Array.isArray(parsedData.outline)) {
        console.error('Invalid API response structure after parsing:', parsedData);
        throw new Error('Received invalid data structure from API');
      }

      // Transform the API response to match our AIGeneratedArticle interface
      return {
        titles: [parsedData.title, ...(parsedData.alternativeTitles || []).slice(0, 3)],
        selectedTitle: parsedData.title,
        outline: parsedData.outline.map((item: any) => ({
          level: item.level,
          heading: item.heading,
          content: item.content || ''
        })),
        tags: parsedData.tags || planningData.tags || []
      };
    } catch (error: any) {
      console.error('Error generating article:', error);

      // Enhance error with more context
      if (error.response) {
        const enhancedError = new Error(
          `API error (${error.response.status}): ${error.response.data?.error || error.message}`
        );
        (enhancedError as any).originalError = error;
        (enhancedError as any).status = error.response.status;
        throw enhancedError;
      }

      throw error;
    }
  },

  /**
   * Generate social share text for a published article and specific platform
   */
  generateShareText: async (request: ShareTextRequest): Promise<ShareTextResponse> => {
    try {
      const { data } = await apiClient.post<ShareTextResponse>('/api/gen/share-text', request);

      if (!data || typeof data.text !== 'string') {
        console.error('Invalid share-text API response:', data);
        throw new Error('Received invalid data structure from share-text API');
      }

      return data;
    } catch (error: any) {
      console.error('Error generating share text:', error);

      if (error.response) {
        const enhancedError = new Error(
          `API error (${error.response.status}): ${error.response.data?.error || error.message}`
        );
        (enhancedError as any).originalError = error;
        (enhancedError as any).status = error.response.status;
        throw enhancedError;
      }

      throw error;
    }
  },

  /**
   * Regenerate article suggestions based on planning data
   * @param planningData The article planning data
   * @returns AI-generated article suggestions
   */
  regenerateArticle: async (planningData: ArticlePlanningData): Promise<AIGeneratedArticle> => {
    // Add a flag to indicate this is a regeneration request
    const regenerationData = {
      ...planningData,
      isRegeneration: true
    };

    // Use the same generateArticle method which now handles the content property correctly
    return articleAiApi.generateArticle(regenerationData);
  },

  /**
   * Generate article titles based on content and optional parameters
   * @param request The title generation request data
   * @returns Generated title suggestions
   */
  generateTitles: async (request: TitleGenerationRequest): Promise<TitleGenerationResponse> => {
    try {
      // console.log('Making API call to /api/gen/title with data:', request);

      // Make API call to the title generation endpoint
      const response = await apiClient.post('/api/gen/title', request, {
        headers: request.profileId ? { 'X-Profile-ID': request.profileId } : {}
      });

      // console.log('Title generation API response received:', response.data);

      // Validate the response
      if (!response.data || !response.data.titles || !Array.isArray(response.data.titles)) {
        console.error('Invalid API response structure:', response.data);
        throw new Error('Received invalid data structure from API');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error generating titles:', error);

      // Enhance error with more context
      if (error.response) {
        const enhancedError = new Error(
          `API error (${error.response.status}): ${error.response.data?.error || error.message}`
        );
        (enhancedError as any).originalError = error;
        (enhancedError as any).status = error.response.status;
        throw enhancedError;
      }

      throw error;
    }
  },

  /**
   * Used by article creation wizard.
   * 
   * Send a message to the article chat AI
   * @param request The chat request data
   * @returns The AI's response
   */
  chat: async (request: ArticleChatRequest, signal?: AbortSignal): Promise<ArticleChatResponse> => {
    try {
      // console.log('Making API call to /api/gen/chat with messages:', request.messages);

      const response = await apiClient.post('/api/gen/chat', request, { signal });

      // console.log('Chat API response received:', response.data);

      if (!response.data || typeof response.data.content !== 'string') {
        console.error('Invalid API response structure:', response.data);
        throw new Error('Received invalid data structure from chat API');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error in article chat:', error);

      if (error.response) {
        const enhancedError = new Error(
          `API error (${error.response.status}): ${error.response.data?.error || error.message}`
        );
        (enhancedError as any).originalError = error;
        (enhancedError as any).status = error.response.status;
        throw enhancedError;
      }

      throw error;
    }
  },

  /**
   * Cancel an active chat request
   * @param messageId The message ID to cancel
   */
  cancelChat: async (messageId: string): Promise<void> => {
    try {
      await apiClient.post('/api/assistant/cancel', { messageId });
    } catch (error) {
      console.error('Error cancelling chat:', error);
      // We don't throw here as cancellation is best-effort
    }
  },

  /**
   * Used by AI agent mode, to modify or edit articles by chatting
   * 
   * Edit an article using AI based on an instruction and message history
   * @param request The article edit request data
   * @returns The edited content and summary message
   */
  editArticle: async (request: ArticleEditRequest, signal?: AbortSignal): Promise<EditArticleResponse> => {
    try {
      const { articleId, ...payload } = request;
      // console.log(`Making API call to /api/gen/ai/edit/${articleId} with data:`, payload);

      const response = await apiClient.post(`/api/gen/ai/edit/${articleId}`, payload, { 
        signal,
        headers: request.profileId ? { 'X-Profile-ID': request.profileId } : {}
      });

      // console.log('Article edit API response received:', response.data);

      if (typeof response.data.message !== 'string' || typeof response.data.hasContentChanges !== 'boolean') {
        console.error('Invalid API response structure:', response.data);
        throw new Error('Received invalid data structure from edit API');
      }

      // Resolve references if present (convert fake URLs to real URLs)
      if (response.data.references) {
        const { resolveReferences } = await import('@/utils/reference-resolver');
        const references = response.data.references;

        // Resolve references in all potential content fields
        if (response.data.content) {
          response.data.content = resolveReferences(response.data.content, references);
        }
        if (response.data.replacement) {
          response.data.replacement = resolveReferences(response.data.replacement, references);
        }
        if (response.data.message) {
          response.data.message = resolveReferences(response.data.message, references);
        }

        const resolvedContent = response.data.content;

        // If full content was resolved, save back to DO for consistency
        if (resolvedContent && response.data.hasContentChanges) {
          console.log('[Article AI] Resolved references in content, syncing with backend:', {
            referencesCount: Object.keys(references).length,
            articleId
          });

          // Import articlesApi dynamically to avoid circular dependency
          const { articlesApi } = await import('@/app/api/articles');
          await articlesApi.saveContent(articleId, resolvedContent);
        }

        return response.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error in article edit:', error);

      if (error.response) {
        // Prefer 'message' for user-friendly text, fallback to 'error' code
        const userMessage = error.response.data?.message || error.response.data?.error || error.message;
        const enhancedError = new Error(userMessage);
        (enhancedError as any).originalError = error;
        (enhancedError as any).status = error.response.status;
        (enhancedError as any).retryable = error.response.data?.retryable ?? true;
        throw enhancedError;
      }

      throw error;
    }
  },


};





